import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
// Recharts note: without an explicit XAxis, the Tooltip's `label` falls back
// to the data-point index (0,1,2,…), which reads like a misleading "+1
// every point, never decreases" counter. Hide the axis but key it on
// decision_ts so the tooltip label is the actual timestamp.
import {
  fetchDecisionActivity,
  fetchLivePositions,
  fetchTradingPerformance,
  type LivePosition,
  type TradingPerformanceTicker,
} from "../api/client";
import { AccountSummaryCard } from "./AccountSummaryCard";

// PM-style report: account-wide P&L + per-ticker rollups so you can answer
// "is each name making me money?" at a glance. Data is counterfactual — it
// uses ensemble_action × next-bar price change, which matches what we'd
// have earned if we executed every signal. Real broker P&L is in the
// AccountSummaryCard at the top.

const DAY_OPTIONS: { label: string; days: number }[] = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "1y", days: 365 },
];

function fmtPct(v: number | null | undefined, digits = 2) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}
function fmtUsd(v: number | null | undefined) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtSignedUsd(v: number | null | undefined) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  const s = v >= 0 ? "+" : "−";
  return `${s}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtHold(hours: number | null | undefined) {
  if (hours === undefined || hours === null || Number.isNaN(hours)) return "—";
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}
function signedPct(v: number | null | undefined, digits = 2) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  const s = v >= 0 ? "+" : "";
  return `${s}${(v * 100).toFixed(digits)}%`;
}
function pnlColor(v: number | null | undefined) {
  if (v === undefined || v === null || Number.isNaN(v)) return "#94a3b8";
  if (v > 0) return "#22c55e";
  if (v < 0) return "#ef4444";
  return "#94a3b8";
}

function SignalChips({ counts }: { counts: Record<string, number> }) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
  const order = ["BUY", "SELL_SHORT", "SELL_REDUCE", "HOLD"];
  const known = order.filter((k) => counts[k]);
  // Surface any signal types not in the canonical list at the end, so a
  // future signal name shows up rather than silently disappearing.
  const extras = Object.keys(counts).filter((k) => !order.includes(k));
  const colors: Record<string, string> = {
    BUY: "#22c55e",
    SELL_SHORT: "#ef4444",
    SELL_REDUCE: "#fbbf24",
    HOLD: "#64748b",
  };
  return (
    <div className="report-signal-chips">
      {[...known, ...extras].map((k) => (
        <span
          key={k}
          className="report-signal-chip"
          title={`${k}: ${counts[k]} / ${total} (${Math.round((counts[k] / total) * 100)}%)`}
          style={{
            color: colors[k] ?? "#94a3b8",
            borderColor: colors[k] ?? "#334155",
          }}
        >
          {k.replace("SELL_", "S/").replace("S/SHORT", "SHORT").replace("S/REDUCE", "REDUCE")} {counts[k]}
        </span>
      ))}
    </div>
  );
}

function Sparkline({ data, color }: { data: { decision_ts: string; cum_return_pct: number }[]; color: string }) {
  if (data.length < 2) {
    return <div style={{ height: 60, color: "#475569", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center" }}>not enough data</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={60}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
        <defs>
          <linearGradient id={`grad-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.4} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        {/* Hidden axis exists only so the Tooltip's label is the decision
            timestamp, not the row index. See note at top of file. */}
        <XAxis dataKey="decision_ts" hide />
        <Tooltip
          contentStyle={{ background: "#0b1220", border: "1px solid #1e293b", fontSize: 11 }}
          formatter={(v: number) => [`${(v * 100).toFixed(2)}%`, "Strategy return"]}
          labelFormatter={(l) => String(l).replace("T", " ").slice(0, 16)}
        />
        <Area
          type="monotone"
          dataKey="cum_return_pct"
          stroke={color}
          strokeWidth={1.5}
          fill={`url(#grad-${color.replace("#", "")})`}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function LivePositionsCard() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["live-positions"],
    queryFn: fetchLivePositions,
    // Broker calls are real network — don't auto-refresh aggressively.
    // 60s background refresh is enough for "what's at risk right now."
    refetchInterval: 60 * 1000,
  });

  if (isLoading) {
    return <div className="card" style={{ color: "#94a3b8" }}>Loading live positions…</div>;
  }
  if (error) {
    return (
      <div className="card" style={{ color: "#fca5a5" }}>
        Live positions error: {String(error)}{" "}
        <button onClick={() => refetch()} style={{ marginLeft: 8 }}>Retry</button>
      </div>
    );
  }
  if (!data) return null;

  const totalUnrealizedColor = pnlColor(data.total_unrealized_pl);

  return (
    <div className="card">
      <div className="report-positions-header">
        <h3>Live broker positions</h3>
        <div className="report-positions-meta">
          {data.position_count} open · gross {fmtUsd(data.total_market_value)} ·
          {" "}<span style={{ color: totalUnrealizedColor, fontWeight: 600 }}>
            unrealized {fmtSignedUsd(data.total_unrealized_pl)}
          </span>
          {isFetching && " · refreshing…"}
        </div>
      </div>

      {data.position_count === 0 ? (
        <div style={{ color: "#94a3b8", fontSize: 13 }}>No open positions.</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Side</th>
                <th className="num">Qty</th>
                <th className="num">Avg entry</th>
                <th className="num">Current</th>
                <th className="num">Market value</th>
                <th className="num">Unrealized $</th>
                <th className="num">Unrealized %</th>
                <th className="num">Held</th>
              </tr>
            </thead>
            <tbody>
              {data.positions.map((p: LivePosition) => (
                <tr key={`${p.ticker}-${p.side}`}>
                  <td>
                    <Link to={`/symbols/${encodeURIComponent(p.ticker)}`} style={{ color: "#e2e8f0", textDecoration: "none", fontWeight: 600 }}>
                      {p.ticker}
                    </Link>
                    {!p.in_trading_roster && (
                      <span
                        className="report-position-stale-chip"
                        title="Position held in a ticker that is not currently in the paper-live roster. Usually means a retired model with shares still open."
                      >
                        STALE
                      </span>
                    )}
                  </td>
                  <td style={{ color: p.side === "short" ? "#fca5a5" : "#86efac", fontSize: 12 }}>
                    {p.side}
                  </td>
                  <td className="num">{p.qty.toLocaleString()}</td>
                  <td className="num">{fmtUsd(p.avg_entry_price)}</td>
                  <td className="num">{fmtUsd(p.current_price)}</td>
                  <td className="num">{fmtUsd(p.market_value)}</td>
                  <td className="num" style={{ color: pnlColor(p.unrealized_pl), fontWeight: 600 }}>
                    {fmtSignedUsd(p.unrealized_pl)}
                  </td>
                  <td className="num" style={{ color: pnlColor(p.unrealized_plpc) }}>
                    {signedPct(p.unrealized_plpc)}
                  </td>
                  <td className="num" style={{ color: "#94a3b8", fontSize: 12 }}>
                    {fmtHold(p.hold_duration_hours)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="report-positions-footer">
        Live broker snapshot · refreshes every 60s · {String(data.generated_at).slice(0, 19)} UTC
      </div>
    </div>
  );
}

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

function DecisionActivityCard({ days }: { days: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["decision-activity", days],
    queryFn: () => fetchDecisionActivity(days),
    refetchInterval: 5 * 60 * 1000,
  });

  // Recharts wants flat rows. Inline each bucket's signal_counts for the
  // stacked bar to consume directly.
  const chartData = useMemo(() => {
    return (data?.buckets ?? []).map((b) => ({
      et_time: b.et_time,
      BUY: b.by_signal.BUY ?? 0,
      SELL_SHORT: b.by_signal.SELL_SHORT ?? 0,
      SELL_REDUCE: b.by_signal.SELL_REDUCE ?? 0,
      HOLD: b.by_signal.HOLD ?? 0,
    }));
  }, [data]);

  const dowMax = Math.max(1, ...(data?.by_day_of_week ?? [1]));

  if (isLoading) {
    return <div className="card" style={{ color: "#94a3b8" }}>Loading activity…</div>;
  }
  if (error) {
    return <div className="card" style={{ color: "#fca5a5" }}>Activity error: {String(error)}</div>;
  }
  if (!data || data.total_decisions === 0) {
    return (
      <div className="card" style={{ color: "#94a3b8" }}>
        No decisions in the last {days}d.
      </div>
    );
  }

  return (
    <div className="card">
      <div className="report-activity-card-header">
        <h3>Decision activity by time of day (ET)</h3>
        <div className="report-activity-card-meta">
          {data.total_decisions.toLocaleString()} decisions · {data.bucket_minutes}-min buckets
          {data.outside_market_hours > 0 && ` · ${data.outside_market_hours} outside RTH`}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="et_time" tick={{ fill: "#94a3b8", fontSize: 10 }} interval={1} />
          <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
          <Tooltip
            contentStyle={{ background: "#0b1220", border: "1px solid #1e293b", fontSize: 12 }}
            cursor={{ fill: "#1e293b", opacity: 0.4 }}
          />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
          <Bar dataKey="BUY" stackId="a" fill="#22c55e" />
          <Bar dataKey="SELL_SHORT" stackId="a" fill="#ef4444" />
          <Bar dataKey="SELL_REDUCE" stackId="a" fill="#fbbf24" />
          <Bar dataKey="HOLD" stackId="a" fill="#475569" />
        </BarChart>
      </ResponsiveContainer>

      <div className="report-activity-dow">
        {DOW_LABELS.map((label, i) => {
          const v = data.by_day_of_week[i] ?? 0;
          // Bar widths normalize against the busiest day so relative
          // weight is visible at a glance.
          const pct = (v / dowMax) * 100;
          return (
            <div key={label} className="report-activity-dow-cell">
              <div className="report-activity-dow-track">
                <div className="report-activity-dow-bar" style={{ height: `${pct}%` }} />
              </div>
              <div className="report-activity-dow-label">{label}</div>
              <div className="report-activity-dow-count">{v.toLocaleString()}</div>
            </div>
          );
        })}
      </div>
      <div className="report-activity-footer">
        Buckets cover NYSE regular hours 09:30–16:00 ET. Open-bar clustering shows up as a tall first bucket.
      </div>
    </div>
  );
}

function TickerCard({ row }: { row: TradingPerformanceTicker }) {
  const color = pnlColor(row.cum_return_pct);
  return (
    <Link
      to={`/symbols/${encodeURIComponent(row.ticker)}`}
      className="report-ticker-card"
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <div className="report-ticker-card-header">
        <strong className="report-ticker-card-symbol">{row.ticker}</strong>
        <span className="report-ticker-card-return" style={{ color }}>
          {signedPct(row.cum_return_pct)}
        </span>
      </div>
      <div style={{ marginTop: 6 }}>
        <Sparkline data={row.equity_curve} color={color} />
      </div>
      <div className="report-ticker-card-stats">
        <div>
          <div>Win rate</div>
          <div>{fmtPct(row.win_rate, 0)}</div>
        </div>
        <div>
          <div>Decisions</div>
          <div>{row.decision_count}</div>
        </div>
        <div>
          <div>Max DD</div>
          <div style={{ color: pnlColor(row.max_drawdown_pct) }}>{fmtPct(row.max_drawdown_pct)}</div>
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        <SignalChips counts={row.signal_counts} />
      </div>
      <div className="report-ticker-card-footer">
        7d {signedPct(row.last_7d_cum_return_pct)} · last decision {String(row.last_decision_ts).slice(5, 16).replace("T", " ")}
      </div>
    </Link>
  );
}

function MoversTable({ title, rows, color }: {
  title: string;
  rows: TradingPerformanceTicker[];
  color: string;
}) {
  return (
    <div className="card report-movers-card">
      <h3>{title}</h3>
      {rows.length === 0 ? (
        <div style={{ color: "#475569", fontSize: 12 }}>—</div>
      ) : (
        <table>
          <tbody>
            {rows.map((r) => (
              <tr key={r.ticker}>
                <td>
                  <Link to={`/symbols/${encodeURIComponent(r.ticker)}`}>
                    {r.ticker}
                  </Link>
                </td>
                <td className="num" style={{ color, fontWeight: 600 }}>
                  {signedPct(r.cum_return_pct)}
                </td>
                <td className="num" style={{ color: "#64748b", fontSize: 11 }}>
                  {r.decision_count} dec
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function ReportsPage() {
  const [days, setDays] = useState(30);

  const { data, isLoading, error } = useQuery({
    queryKey: ["trading-performance", days],
    queryFn: () => fetchTradingPerformance(days),
    // Same cadence as AccountSummaryCard so the page feels coherent.
    refetchInterval: 5 * 60 * 1000,
  });

  const { winners, losers } = useMemo(() => {
    const t = data?.tickers ?? [];
    return {
      winners: [...t].filter((r) => r.cum_return_pct > 0).slice(0, 5),
      losers: [...t].filter((r) => r.cum_return_pct < 0).sort((a, b) => a.cum_return_pct - b.cum_return_pct).slice(0, 5),
    };
  }, [data]);

  return (
    <div>
      <AccountSummaryCard mode="paper" />
      <LivePositionsCard />

      <div className="toolbar">
        <span style={{ color: "#94a3b8", fontSize: 13 }}>Window:</span>
        {DAY_OPTIONS.map((opt) => (
          <button
            key={opt.days}
            onClick={() => setDays(opt.days)}
            className={`report-window-pill${opt.days === days ? " active" : ""}`}
          >
            {opt.label}
          </button>
        ))}
        <span style={{ color: "#64748b", fontSize: 12, marginLeft: 8 }}>
          {isLoading ? "loading…" : data ? `${data.totals.ticker_count} tickers · ${data.totals.decision_count} decisions` : ""}
        </span>
      </div>

      {error && <div className="card" style={{ color: "#fca5a5" }}>{String(error)}</div>}

      {/* Section divider: everything below this banner is strategy /
          counterfactual metrics, not real money. Sits OUTSIDE the rollup
          card so it visually scopes to all subsequent sections (rollup,
          top winners/losers, per-ticker grid) instead of looking attached
          to just the 4 stat numbers. */}
      {data && data.totals.ticker_count > 0 && (
        <>
          <div className="report-section-banner">
            <div className="report-section-banner-label">
              Strategy view — everything below is counterfactual
            </div>
            The rollup, winners/losers, and per-ticker cards below are{" "}
            <code>sign(ensemble_action) × next-bar % move</code>,{" "}
            compounded across every decision in the window. They reflect <em>signal skill</em>,
            not realized money — no position sizing, fills, or costs.
            For actual broker P&L, see the <strong>Account</strong> card and{" "}
            <strong>Live broker positions</strong> table at the top.
          </div>

          <div className="card report-rollup">
            <div>
              <div className="report-rollup-cell-label">Winning tickers</div>
              <div className="report-rollup-cell-value" style={{ color: "#22c55e" }}>{data.totals.winning_count}</div>
            </div>
            <div>
              <div className="report-rollup-cell-label">Losing tickers</div>
              <div className="report-rollup-cell-value" style={{ color: "#ef4444" }}>{data.totals.losing_count}</div>
            </div>
            <div>
              <div className="report-rollup-cell-label">Median return</div>
              <div className="report-rollup-cell-value" style={{ color: pnlColor(data.totals.median_cum_return_pct) }}>
                {signedPct(data.totals.median_cum_return_pct)}
              </div>
            </div>
            <div>
              <div className="report-rollup-cell-label">Total decisions</div>
              <div className="report-rollup-cell-value" style={{ color: "#e2e8f0" }}>{data.totals.decision_count.toLocaleString()}</div>
            </div>
          </div>
        </>
      )}

      {data && data.totals.ticker_count > 0 && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <MoversTable title="Top winners" rows={winners} color="#22c55e" />
          <MoversTable title="Top losers" rows={losers} color="#ef4444" />
        </div>
      )}

      <DecisionActivityCard days={days} />

      {data && data.totals.ticker_count > 0 && (
        <div className="report-ticker-grid">
          {data.tickers.map((row) => (
            <TickerCard key={row.ticker} row={row} />
          ))}
        </div>
      )}

      {data && data.totals.ticker_count === 0 && (
        <div className="card" style={{ color: "#94a3b8" }}>
          No tickers are currently paper-live. Promote a model on the Run history or TPE pages,
          or enable a new ticker on the Tickers admin page, to start populating this report.
        </div>
      )}
    </div>
  );
}
